/**
 * Shared UI pieces for the admin upload surfaces (Data Upload page,
 * Upload History page, UploadDetailDialog).
 */
import React from 'react';
import { Box, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Typography } from '@mui/material';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';

export const uploadStatusChipSx = (palette, status) => {
  const map = {
    completed: { bg: palette.accentBg, c: palette.accent },
    error: { bg: palette.dangerBg, c: palette.danger },
  };
  const t = map[status] || { bg: palette.surfaceHover, c: palette.textMuted };
  return {
    height: 20,
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    bgcolor: t.bg,
    color: t.c,
  };
};

/** Table of {sheet, row, message} entries (errors or warnings). */
export function IssueTable({ rows, tone = 'danger', maxHeight = 280 }) {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const accentColor = tone === 'danger' ? palette.danger : palette.warning;
  if (!rows?.length) return null;
  return (
    <TableContainer sx={{ maxHeight, border: `1px solid ${palette.divider}`, borderRadius: '10px' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={recipes.headCellSx}>Sheet</TableCell>
            <TableCell align="right" sx={recipes.headCellSx}>Row</TableCell>
            <TableCell sx={recipes.headCellSx}>Detail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((d, i) => (
            <TableRow key={i} sx={recipes.bodyRowSx}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>{d.sheet}</Typography>
              </TableCell>
              <TableCell align="right">
                <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.8rem', color: accentColor }}>{d.row}</Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{d.message}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Honest cap caption: "Showing X of Y" only when rows were truncated. */
export function CapCaption({ shown, total, noun }) {
  const { palette } = useThemeMode();
  if (!total || shown >= total) return null;
  return (
    <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mt: 1 }}>
      Showing {shown.toLocaleString()} of {total.toLocaleString()} {noun}
    </Typography>
  );
}
